import { formatUnits } from 'viem';
import { formatDifficulty, formatHashrate } from '@hashcurve/miner/spec';

const SUBSCRIPT = '₀₁₂₃₄₅₆₇₈₉';
const toSubscript = (n: number) => String(n).split('').map((d) => SUBSCRIPT[Number(d)]).join('');
/** "1.2300" -> "1.23", "800" -> "800" */
const stripZeros = (s: string) => (s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s);

/**
 * Decimal string -> compact display with `sig` significant digits.
 * Values below 0.0001 use subscript zero notation: 0.000000000938 -> "0.0₉938".
 */
export function formatDecimal(value: string, sig = 4): string {
  const neg = value.startsWith('-');
  const v = neg ? value.slice(1) : value;
  const [intPart = '0', frac = ''] = v.split('.');
  const sign = neg ? '-' : '';
  if (intPart !== '0' && intPart !== '') {
    const n = Number(v);
    const digits = Math.max(0, sig - intPart.length);
    return sign + n.toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: 0 });
  }
  const zeros = frac.match(/^0*/)?.[0].length ?? 0;
  if (zeros === frac.length) return '0';
  const significant = frac.slice(zeros, zeros + sig).replace(/0+$/, '');
  if (zeros >= 4) return `${sign}0.0${toSubscript(zeros)}${significant}`;
  return `${sign}0.${'0'.repeat(zeros)}${significant}`;
}

/** wei -> "0.0831" (no unit) */
export const formatEthValue = (wei: bigint, sig = 4) => formatDecimal(formatUnits(wei, 18), sig);
/** wei -> "0.0831 ETH" */
export const formatEth = (wei: bigint, sig = 4) => `${formatEthValue(wei, sig)} ETH`;
/** ETH amount given as a float (API *Float fields) */
export function formatEthFloat(x: number | null | undefined, sig = 4): string {
  if (x === null || x === undefined || !Number.isFinite(x)) return '—';
  return `${formatDecimal(stripZeros(x.toFixed(20)), sig)} ETH`;
}

/** token base units -> "4,000,000" (fractions only for small amounts) */
export function formatTokens(amount: bigint, decimals = 18): string {
  const s = formatUnits(amount, decimals);
  const n = Number(s);
  if (n === 0) return '0';
  if (n >= 1000) return Math.floor(n).toLocaleString('en-US');
  if (n >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return formatDecimal(s, 4);
}

export function formatPct(x: number | null | undefined, digits = 1): string {
  if (x === null || x === undefined || !Number.isFinite(x)) return '—';
  return `${(x * 100).toFixed(digits)}%`;
}

export function formatSignedPct(x: number | null | undefined): string {
  if (x === null || x === undefined || !Number.isFinite(x)) return '—';
  const s = (x * 100).toFixed(1);
  return x > 0 ? `+${s}%` : `${s}%`;
}

/** Difficulty (expected hashes) with a thin space before the suffix: "18.4 G" */
export function formatWork(difficulty: number | bigint | null | undefined): string {
  if (difficulty === null || difficulty === undefined) return '—';
  if (typeof difficulty === 'number' && !Number.isFinite(difficulty)) return '—';
  return formatDifficulty(difficulty).replace(/(\d)([KMGTPE])$/, '$1 $2');
}

export function formatRate(hps: number | null | undefined): string {
  if (hps === null || hps === undefined || !Number.isFinite(hps)) return '—';
  return formatHashrate(hps);
}

/** 0x82f…9e1 */
export function shortAddress(addr: string | null | undefined): string {
  if (!addr) return '—';
  return `${addr.slice(0, 5)}…${addr.slice(-3)}`;
}

/** 0x5446e341…a13bc05b */
export function shortHex(hex: string, head = 10, tail = 8): string {
  if (hex.length <= head + tail + 1) return hex;
  return `${hex.slice(0, head)}…${hex.slice(-tail)}`;
}

/** leading zero hex digits of a 0x-prefixed 32-byte hash */
export function leadingZeroNibbles(hex: string): number {
  const h = hex.startsWith('0x') ? hex.slice(2) : hex;
  const m = h.match(/^0*/);
  return m ? m[0].length : 0;
}

/** unix seconds -> "06:42:11" (local time) */
export function formatClockTime(ts: number): string {
  const d = new Date(ts * 1000);
  return [d.getHours(), d.getMinutes(), d.getSeconds()].map((n) => String(n).padStart(2, '0')).join(':');
}

/** unix seconds -> "25 Sep, 14:05" */
export function formatDateTime(ts: number): string {
  return new Date(ts * 1000).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** seconds -> "0:12", "3:05", "1:02:03", "2d 3h" */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  if (s >= 86400) return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(r).padStart(2, '0')}`;
}

/** seconds ago -> "12 s ago", "4 min ago" */
export function formatAgo(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}
